// https://www.youtube.com/watch?v=sHhgcDYa8jw&ab_channel=DesignCode

import React, { createContext, useEffect, useState, useMemo } from "react";
import { useAppContext, useProductContext } from "~hooks";
import fetch from "isomorphic-fetch";
import Client from "shopify-buy";
import ls from "~utils/localStorage.js";
import { DISCOUNT_TIERS } from "~constants/discounts";

const client = Client.buildClient(
  {
    domain: process.env.GATSBY_MYSHOPIFY_URL,
    storefrontAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN
  },
  fetch
);
const defaultValues = {
  cartItems: [],
  loading: false,
  addVariantToCart: () => {},
  removeLineItem: () => {},
  client,
  checkout: {
    id: ``,
    lineItems: [],
    webUrl: ``
  }
};

export const CartContext = createContext(defaultValues);

const LOCAL_STORAGE_CHECKOUT_ID_KEY = `lgf_shopify_checkout_id`;
const LOCAL_STORAGE_CART_KEY = `lgf_shopify_cart`;
const CART_LAST_UPDATED_KEY = `lgf_cart_last_updated`;
const CART_EXPIRY_THRESHOLD_DAYS = 7;

const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(defaultValues.cartItems);
  const [checkout, setCheckout] = useState(defaultValues.checkout);
  const [isCartUpsellWindowOpen, setIsCartUpsellWindowOpen] = useState(false);
  const [cartUpsellProductIds, setCartUpsellProductIds] = useState(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [hasCartInitialised, setHasCartInitialised] = useState(false);

  const { setLoading, setIsCartOpen, isCartOpen } = useAppContext();
  const {
    getVariantProductData,
    productData: products,
    checkIsProductInStock
  } = useProductContext();

  const updateCartLastUpdatedLocalStorage = () => {
    const currentTimeMilliseconds = new Date().getTime();
    ls.setItem(CART_LAST_UPDATED_KEY, currentTimeMilliseconds);
  };

  // Close in-cart add when cart is closed
  useEffect(() => {
    if (!isCartOpen) {
      // Timeout to prevent both animations happening at same time, looks strange
      setTimeout(() => {
        setIsCartUpsellWindowOpen(false);
      }, 300);
    }
  }, [isCartOpen]);

  // Parse saved cart from localstorage to state
  useEffect(() => {
    const data = ls.getItem(LOCAL_STORAGE_CART_KEY);
    if (data) {
      setCartItems(data);
    }
  }, []);

  // Sync localstorage with cart
  useEffect(() => {
    ls.setItem(LOCAL_STORAGE_CART_KEY, cartItems);
    updateCartLastUpdatedLocalStorage();
  }, [cartItems]);

  // Sync localstorage with checkout
  useEffect(() => {
    ls.setItem(LOCAL_STORAGE_CHECKOUT_ID_KEY, checkout.id);
  }, [checkout]);

  const createNewCheckout = async () => {
    const newCheckout = await client.checkout.create();
    setCheckout(newCheckout);
  };

  const reset = () => {
    createNewCheckout();
    setCartItems(defaultValues.cartItems);
    setHasCartInitialised(true);
  };

  // Initial checkout setup
  const existingCheckoutId = ls.getItem(LOCAL_STORAGE_CHECKOUT_ID_KEY); // Don't know why but these don't work...
  const timeCheckoutLastUsedMilliseconds = ls.getItem(CART_LAST_UPDATED_KEY); // ...inside the useEffect

  useEffect(() => {
    const initialiseCheckout = async () => {
      if (!existingCheckoutId || !timeCheckoutLastUsedMilliseconds) {
        reset();
        return;
      }

      try {
        const existingCheckout = await client.checkout.fetch(
          existingCheckoutId
        );

        // Reset if checkout already completed
        if (existingCheckout.completedAt) {
          reset();
          return;
        }

        // Reset if last site visit was more than CART_EXPIRY_THRESHOLD_DAYS ago
        const currentTimeMilliseconds = new Date().getTime();
        const differenceMilliseconds =
          currentTimeMilliseconds - timeCheckoutLastUsedMilliseconds;
        const millisecondsInDay = 1000 * 60 * 60 * 24;
        const daysDifference = differenceMilliseconds / millisecondsInDay;

        if (daysDifference > CART_EXPIRY_THRESHOLD_DAYS) {
          reset();
          return;
        }
        setCheckout(existingCheckout);
        setHasCartInitialised(true);
      } catch (err) {
        console.error(`Resetting cart due to error: ${err}}`);
        reset();
      }
    };
    initialiseCheckout();
  }, []);

  const addVariantsToCart = async (variantIds, quantity) => {
    setLoading(true);

    if (!checkout.id) {
      throw new Error(`No checkout ID assigned.`);
    }

    try {
      const lineItems = [];

      variantIds.forEach((variantId) => {
        lineItems.push({
          variantId,
          quantity
        });
      });

      // Add to checkout
      const res = await client.checkout.addLineItems(checkout.id, lineItems);
      setCheckout(res);

      // Update cartItems
      let updatedCart = [...cartItems]; // Needs to be new array to trigger localstorage update

      variantIds.forEach((variantId) => {
        const existingItemInCart = updatedCart.find(
          (item) => item.variantId === variantId
        );

        if (existingItemInCart) {
          // Product already in cart
          updatedCart = updatedCart.map((item) =>
            item.variantId === variantId
              ? {
                  ...item,
                  quantity: item.quantity + quantity
                }
              : item
          );
        } else {
          // Create new product cart item
          const newItem = {
            variantId,
            quantity
          };
          updatedCart.push(newItem);
        }
      });

      setCartItems(updatedCart);
      setIsCartOpen(true);
    } catch (error) {
      console.error(`Error in addVariantsToCart: ${error}`);
    } finally {
      setIsCartUpsellWindowOpen(false);
      setLoading(false);
    }
  };

  const getLineItemId = (variantId) => {
    let lineItemId = ``;
    for (let i = 0; i < checkout.lineItems.length; i++) {
      const item = checkout.lineItems[i];
      if (item.variant.id === variantId) {
        lineItemId = item.id;
        break;
      }
    }
    if (!lineItemId) {
      throw new Error(`Product not in cart`);
    }

    return lineItemId;
  };

  const removeLineItem = async (variantId) => {
    setLoading(true);

    try {
      if (!checkout.lineItems?.[0]) throw new Error(`Cart is empty`);

      const lineItemId = getLineItemId(variantId);

      const res = await client.checkout.removeLineItems(checkout.id, [
        lineItemId
      ]);
      setCheckout(res);

      const updatedCart = cartItems.filter(
        (item) => item.variantId !== variantId
      );
      setCartItems(updatedCart);
    } catch (error) {
      throw new Error(`Error in removeLineItem: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantityOfLineItem = async (variantId, updatedQuantity) => {
    setLoading(true);

    try {
      const lineItemId = getLineItemId(variantId);
      const lineItemToUpdate = [
        {
          id: lineItemId,
          quantity: updatedQuantity
        }
      ];
      const res = await client.checkout.updateLineItems(
        checkout.id,
        lineItemToUpdate
      );
      setCheckout(res);

      const updatedCart = cartItems.map((item) =>
        item.variantId === variantId
          ? { ...item, quantity: updatedQuantity }
          : { ...item }
      );
      setCartItems(updatedCart);
    } catch (error) {
      console.error(`Error in updateQuantityOfLineItem: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const numberOfItemsInCart = cartItems.reduce(
    (acc, item) => acc + item.quantity,
    0
  );

  const getCartDiscountPercentage = (cartItemsQuantity) => {
    if (cartItemsQuantity <= 1) return 0;
    const minimumDiscount = DISCOUNT_TIERS[0];
    const maximumDiscount = DISCOUNT_TIERS[DISCOUNT_TIERS.length - 1];

    if (cartItemsQuantity < minimumDiscount.numberOfItems) return 0;
    if (cartItemsQuantity >= maximumDiscount.numberOfItems)
      return maximumDiscount.discountPercentage;

    const getRecursiveDiscountPercentage = (itemsQuantity) => {
      const discountTier = DISCOUNT_TIERS.find(
        (tier) => tier.numberOfItems === itemsQuantity
      );
      if (!discountTier) {
        return getRecursiveDiscountPercentage(itemsQuantity - 1);
      }
      return discountTier.discountPercentage;
    };

    return getRecursiveDiscountPercentage(cartItemsQuantity);
  };

  const currentCartDiscountPercentage =
    getCartDiscountPercentage(numberOfItemsInCart);

  const getDiscountedItemPrice = (price) => {
    const discountPercentage = getCartDiscountPercentage(numberOfItemsInCart);
    if (discountPercentage === 0) return price;
    return price * (1 - discountPercentage / 100);
  };

  const hasReachedMaximumDiscount = (itemsQuantity) =>
    itemsQuantity >= DISCOUNT_TIERS[DISCOUNT_TIERS.length - 1].numberOfItems;

  const getUpsellHintText = (itemsQuantity) => {
    if (!itemsQuantity) return ``;

    const nextTierDiscountPercentage = getCartDiscountPercentage(
      itemsQuantity + 1
    );
    const maximumDiscountPercentage =
      DISCOUNT_TIERS[DISCOUNT_TIERS.length - 1].discountPercentage;
    const discountHint = `Add another to your cart to save ${nextTierDiscountPercentage}%`;

    return hasReachedMaximumDiscount(itemsQuantity)
      ? `Saving ${maximumDiscountPercentage}% off cart total!`
      : discountHint;
  };

  const getCartPrice = () => {
    let preDiscountedPriceTally = 0;

    cartItems.forEach(({ variantId, quantity }) => {
      const productData = getVariantProductData(variantId);
      preDiscountedPriceTally += productData.price.amount * quantity;
    });

    const discountedPrice = getDiscountedItemPrice(preDiscountedPriceTally);

    return {
      preDiscounted: preDiscountedPriceTally,
      discounted: discountedPrice,
      isDiscounted: discountedPrice !== preDiscountedPriceTally
    };
  };

  const getCartUpsellProductId = () => {
    const inStockProducts = products.filter((product) =>
      checkIsProductInStock(product.productId)
    );

    const productsSortedByPrice = inStockProducts
      .filter((product) => product?.price?.amount)
      .sort((a, b) => b.price.amount - a.price.amount);

    const productIdsInCart = [];
    cartItems.forEach((cartItem) => {
      const { productId } = getVariantProductData(cartItem.variantId);
      if (productIdsInCart.includes(productId)) return;
      productIdsInCart.push(productId);
    });

    const productsNotInCart = productsSortedByPrice.filter(
      (product) => !productIdsInCart.includes(product.productId)
    );

    const firstProductNotInCart = productsNotInCart?.[0];

    if (firstProductNotInCart) {
      return firstProductNotInCart.productId;
    }

    return productsSortedByPrice?.[0]?.productId;
  };

  const goToCheckout = async () => {
    setIsCheckingOut(true);
    window.location = checkout.webUrl;
  };

  const memoisedContextValues = useMemo(() => ({
    ...defaultValues,
    addVariantsToCart,
    removeLineItem,
    updateQuantityOfLineItem,
    cartItems,
    checkout,
    isCartUpsellWindowOpen,
    setIsCartUpsellWindowOpen,
    cartUpsellProductIds,
    setCartUpsellProductIds,
    goToCheckout,
    isCheckingOut,
    hasCartInitialised,
    numberOfItemsInCart,
    getCartDiscountPercentage,
    currentCartDiscountPercentage,
    getDiscountedItemPrice,
    getUpsellHintText,
    hasReachedMaximumDiscount,
    getCartPrice,
    getCartUpsellProductId
  }));

  return (
    <CartContext.Provider value={memoisedContextValues}>
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
